/**
 * Parses time durations from markdown text and converts them to minutes
 * Supports various formats:
 * - "10 min" = 10 minutes
 * - "10 minutes" = 10 minutes
 * - "10-15 minutes" = 15 minutes (uses higher value)
 * - "1.5 hours" = 90 minutes
 * - "1.5 timer" = 90 minutes (Danish typo)
 * - "1.5 time" = 90 minutes (Danish typo)
 * - "10 minutter" = 10 minutes (Danish)
 */

/**
 * Parses a single time string and returns minutes
 * @param {string} timeStr - Time string to parse (e.g., "10 min", "1.5 hours")
 * @returns {number} - Duration in minutes, or 0 if parsing fails
 */
export const parseTimeString = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') return 0;
  
  // Normalize: remove extra whitespace, convert to lowercase
  let normalized = timeStr.trim().toLowerCase();
  
  // Handle range format: "10-15 minutes" or "10,5-15,5 minutes" -> use the higher value
  const rangeMatch = normalized.match(/^(\d+(?:[.,]\d+)?)\s*-\s*(\d+(?:[.,]\d+)?)/);
  if (rangeMatch) {
    const lowerStr = rangeMatch[1].replace(',', '.');
    const higherStr = rangeMatch[2].replace(',', '.');
    const lower = parseFloat(lowerStr);
    const higher = parseFloat(higherStr);
    normalized = `${higher} ${normalized.substring(rangeMatch[0].length).trim()}`;
  }
  
  // Extract number (supports decimals like 1.5 or 1,5 - Danish format)
  const numberMatch = normalized.match(/^(\d+(?:[.,]\d+)?)/);
  if (!numberMatch) return 0;
  
  // Replace comma with period for parsing (Danish decimal separator)
  const numberStr = numberMatch[1].replace(',', '.');
  const number = parseFloat(numberStr);
  if (isNaN(number)) return 0;
  
  // Extract unit (everything after the number)
  const unit = normalized.substring(numberMatch[0].length).trim();
  
  // Handle hours (including Danish typos: "timer", "time")
  if (unit.includes('hour') || unit.includes('timer') || unit.includes('time') || unit === 'h' || unit === 'timer' || unit === 'time') {
    return Math.round(number * 60);
  }
  
  // Handle minutes (including Danish: "minutter")
  if (unit.includes('min') || unit.includes('minutter') || unit === 'm' || unit === '') {
    return Math.round(number);
  }
  
  // Default: assume minutes if no unit or unrecognized unit
  return Math.round(number);
};

/**
 * Extracts time durations from markdown text
 * Looks for <time> tags that contain time information
 * @param {string} markdown - Markdown text to parse
 * @returns {number} - Total duration in minutes
 */
export const parseTimeFromMarkdown = (markdown) => {
  if (!markdown || typeof markdown !== 'string') return 0;
  
  // Match <time> tags: <time>text</time>
  // Use non-greedy match to handle multiple time tags
  const timePattern = /<time>([^<]+)<\/time>/gi;
  
  let totalMinutes = 0;
  let match;
  
  while ((match = timePattern.exec(markdown)) !== null) {
    const timeText = match[1].trim();
    const minutes = parseTimeString(timeText);
    totalMinutes += minutes;
  }
  
  return totalMinutes;
};

/**
 * Parses time durations from multiple markdown texts (e.g., recipe steps)
 * @param {Array<string>} markdownTexts - Array of markdown strings
 * @returns {number} - Total duration in minutes across all texts
 */
export const parseTotalTimeFromSteps = (markdownTexts) => {
  if (!Array.isArray(markdownTexts)) return 0;
  
  return markdownTexts.reduce((total, text) => {
    return total + parseTimeFromMarkdown(text);
  }, 0);
};

